import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useMemo, useState } from 'react';
import { Box, Button, IconButton, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import CustomDialog from '@ui/components/common/Dialog';
import BDrawer from '@ui/components/common/BDrawer';
import { useTranslation } from 'react-i18next';
import { PrimaryGrid } from '@ui/components/common/Grid';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import AssignTicketsDialog from './AssignTicketsDialog';
import ActionDropdown from './components/ActionDropdown';
import ControlledFileUploadDropzone from '@ui/components/common/ControlledFileUploadDropzone';
import { useForm } from 'react-hook-form';
const uploadFileTypes = ['PDF', 'PNG', 'JPG', 'JPEG'];
const initialTickets = [
    {
        id: 'ticket-128749912447',
        name: 'Ticket 128749912447',
        link: 'https://tickets.vdk-happiness.com/128749912447',
        assignedTo: {
            id: 'fate',
            name: 'Fate Basit'
        }
    },
    {
        id: 'ticket-128749912448',
        name: 'Ticket 128749912448',
        link: 'https://tickets.vdk-happiness.com/128749912448',
        assignedTo: {
            id: 'sophie',
            name: 'Sophie Kramer'
        }
    },
    {
        id: 'ticket-128749912449',
        name: 'Ticket 128749912449',
        link: 'https://tickets.vdk-happiness.com/128749912449'
    },
    {
        id: 'ticket-128749912450',
        name: 'Ticket 128749912450',
        link: 'https://tickets.vdk-happiness.com/128749912450'
    },
    {
        id: 'ticket-128749912451',
        name: 'Ticket 128749912451',
        link: 'https://tickets.vdk-happiness.com/128749912451',
        assignedTo: {
            id: 'lena',
            name: 'Lena de Boer'
        }
    }
];
const TicketsDialog = ({ open, onClose }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { control } = useForm({
        defaultValues: {
            ticketUpload: null
        }
    });
    const [tickets, setTickets] = useState(initialTickets);
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 5
    });
    const [assignDialogOpen, setAssignDialogOpen] = useState(false);
    const [assignMode, setAssignMode] = useState('database');
    const [selectedTicketId, setSelectedTicketId] = useState(null);
    const handleFileChange = useCallback((file) => {
        const selectedFile = Array.isArray(file) ? (file[0] ?? null) : file;
        if (selectedFile) {
            toast.success(t('fileUpload.uploadFile') || 'File uploaded');
        }
    }, [t]);
    const handleCopyLink = useCallback(async (link) => {
        try {
            if (navigator?.clipboard?.writeText) {
                await navigator.clipboard.writeText(link);
                toast.success(t('sponsorships.tickets.linkCopied') || 'Ticket link copied to clipboard');
            }
        }
        catch {
            toast.error(t('sponsorships.tickets.copyFailed') || 'Unable to copy link');
        }
    }, [t]);
    const handleDownload = useCallback((link) => {
        window.open(link, '_blank', 'noopener,noreferrer');
    }, []);
    const handleSend = useCallback((ticketName, channel) => {
        const key = channel === 'email'
            ? 'sponsorships.tickets.sendEmailConfirmation'
            : 'sponsorships.tickets.sendSmsConfirmation';
        toast.success(t(key, { ticket: ticketName }) || 'Ticket shared successfully');
    }, [t]);
    const handleSaveAll = useCallback(() => {
        toast.success(t('sponsorships.tickets.saveConfirmation') || 'Tickets saved successfully');
        onClose();
    }, [onClose, t]);
    const handleUnassign = useCallback((ticketId) => {
        setTickets((prev) => prev.map((ticket) => ticket.id === ticketId ? { ...ticket, assignedTo: undefined } : ticket));
        toast.success(t('sponsorships.tickets.unassignSuccess') || 'Ticket unassigned');
    }, [t]);
    const openAssignDialog = useCallback((ticketId, modeToOpen) => {
        setSelectedTicketId(ticketId);
        setAssignMode(modeToOpen);
        setAssignDialogOpen(true);
    }, []);
    const handleAssignConfirm = useCallback(({ mode: selectedMode, collaborator, manual }) => {
        if (!selectedTicketId)
            return;
        if (selectedMode === 'database' && collaborator) {
            setTickets((prev) => prev.map((ticket) => ticket.id === selectedTicketId
                ? { ...ticket, assignedTo: { id: collaborator.id, name: collaborator.person.name } }
                : ticket));
            toast.success(t('sponsorships.tickets.assignSuccess', {
                ticket: selectedTicketId,
                name: collaborator.person.name
            }) || 'Ticket assigned successfully');
        }
        if (selectedMode === 'manual' && manual) {
            const fullName = manual.name?.trim() ||
                [manual.firstName, manual.middleName, manual.lastName]
                    .map((part) => part?.trim())
                    .filter(Boolean)
                    .join(' ');
            setTickets((prev) => prev.map((ticket) => ticket.id === selectedTicketId
                ? {
                    ...ticket,
                    assignedTo: {
                        id: manual.email,
                        name: fullName || manual.email
                    }
                }
                : ticket));
            toast.success(t('sponsorships.tickets.assignSuccess', {
                ticket: selectedTicketId,
                name: fullName || manual.email
            }) || 'Ticket assigned successfully');
        }
    }, [selectedTicketId, t]);
    const columns = useMemo(() => [
        {
            field: 'name',
            headerName: t('sponsorships.tickets.columnName'),
            flex: 1.2,
            minWidth: 220,
            sortable: false,
            renderCell: (params) => (_jsx(Typography, { variant: 'body2', color: 'text.primary', noWrap: true, children: params.value }))
        },
        {
            field: 'link',
            headerName: t('sponsorships.tickets.columnLink'),
            width: 140,
            sortable: false,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => (_jsxs(Box, { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, children: [_jsx(Tooltip, { title: t('sponsorships.tickets.copy'), children: _jsx(IconButton, { size: 'small', color: 'inherit', onClick: () => handleCopyLink(params.value), children: _jsx(Icon, { icon: 'eva:copy-outline', width: 20, height: 20 }) }) }), _jsx(Tooltip, { title: t('sponsorships.tickets.download'), children: _jsx(IconButton, { size: 'small', color: 'inherit', onClick: () => handleDownload(params.value), children: _jsx(Icon, { icon: 'eva:download-outline', width: 20, height: 20 }) }) })] }))
        },
        {
            field: 'assignedTo',
            headerName: t('sponsorships.tickets.columnAssignedTo'),
            flex: 1,
            minWidth: 200,
            sortable: false,
            renderCell: (params) => {
                const assigned = params.value;
                const name = assigned?.name?.trim();
                return (_jsx(Typography, { variant: 'body2', color: 'text.primary', noWrap: true, children: name || t('sponsorships.tickets.unassigned') }));
            }
        },
        {
            field: 'send',
            headerName: ' ',
            width: 160,
            sortable: false,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => (_jsx(ActionDropdown, { row: params.row, onUnassign: handleUnassign, onSend: handleSend, onAssign: openAssignDialog }))
        }
    ], [handleCopyLink, handleDownload, handleSend, handleUnassign, openAssignDialog, t]);
    const uploader = (_jsx(ControlledFileUploadDropzone, { control: control, name: 'ticketUpload', onValueChange: handleFileChange, types: uploadFileTypes, label: t('sponsorships.tickets.uploadLabel') }));
    const actionButtons = (_jsxs(Box, { sx: { display: 'flex', gap: 1.5, width: '100%' }, children: [_jsx(Button, { variant: 'outlined', color: 'inherit', size: 'large', fullWidth: true, onClick: onClose, children: t('actions.cancel') }), _jsx(Button, { variant: 'contained', color: 'inherit', size: 'large', onClick: handleSaveAll, fullWidth: true, endIcon: _jsx(Icon, { icon: 'eva:checkmark-fill' }), children: t('sponsorships.tickets.saveAll') })] }));
    const content = (_jsxs(Box, { sx: {
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            px: { xs: 2, md: 3 },
            py: { xs: 1, md: 2 },
            width: '100%'
        }, children: [uploader, _jsx(PrimaryGrid, { rows: tickets, columns: columns, showFilters: false, showHeaderControls: false, haveSearch: false, paginationMode: 'client', paginationModel: paginationModel, onPaginationModelChange: setPaginationModel, rowCount: tickets.length, disableRowSelectionOnClick: true, hideFooterSelectedRowCount: true, pageSizeOptions: [5, 10], maxHeight: '250px', noRowsText: t('sponsorships.tickets.noTickets') ||
                    t('dataGrid.noRows', {
                        entityPlural: t('sponsorships.tickets.columnName')?.toLowerCase()
                    }) })] }));
    const remainingTickets = useMemo(() => tickets.filter((ticket) => !ticket.assignedTo).length, [tickets]);
    const assignDialog = (_jsx(AssignTicketsDialog, { open: assignDialogOpen, onClose: () => setAssignDialogOpen(false), mode: assignMode, ticketName: selectedTicketId ?? '', remainingTickets: remainingTickets, onConfirm: handleAssignConfirm }));
    if (isMobile) {
        return (_jsxs(_Fragment, { children: [_jsx(BDrawer, { anchor: 'bottom', open: open, onClose: onClose, title: t('sponsorships.tickets.title'), showCloseButton: true, dialogActions: _jsx(Box, { width: '100%', children: actionButtons }), children: content }), assignDialog] }));
    }
    return (_jsxs(_Fragment, { children: [_jsx(CustomDialog, { open: open, onClose: onClose, titleIcon: '/assets/images/starIcon.png', titleWithIcon: t('sponsorships.tickets.title'), backgroundVariant: 'white', maxWidth: 'md', actions: actionButtons, children: content }), assignDialog] }));
};
export default TicketsDialog;
